import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { enhanceRichLinks } from './richLinks';
import './airbnb';
import './google';
import { enhanceCostTables } from './costTable';
import { splitTripSections } from './tripSections';
import { setupInstallBanner, setupHowToSection, scopeManifestToTrip, setHomeScreenName } from './install';

const SLUG_RE = /^[a-z2-9]{8}$/;

function extractNotionPageId(input: string): string | null {
  const cleaned = input.replace(/-/g, '').toLowerCase();
  const match = cleaned.match(/[0-9a-f]{32}/);
  return match ? match[0] : null;
}

function show(id: string): void {
  document.querySelectorAll<HTMLElement>('section[id^="view-"]').forEach((el) => {
    el.hidden = el.id !== id;
  });
}

function setHidden(el: HTMLElement | null, hidden: boolean): void {
  if (el) el.hidden = hidden;
}

// ---------- Router ----------
const pathname = window.location.pathname.replace(/\/+$/, '') || '/';

if (pathname === '/' || pathname === '') {
  show('view-landing');
  void initLanding();
} else if (pathname === '/admin') {
  show('view-admin');
  void initAdmin();
} else {
  const slug = pathname.replace(/^\//, '');
  if (SLUG_RE.test(slug)) {
    show('view-trip');
    scopeManifestToTrip(slug);
    void initTrip(slug);
  } else {
    show('view-notfound');
  }
}

// Add target="_blank" to external links inside the rendered markdown so
// hotel/restaurant URLs etc. open in a new tab rather than navigating away
// from the trip page. Same-origin and anchor links are left alone.
function openExternalLinksInNewTab(container: HTMLElement): void {
  container.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    if (!/^https?:\/\//i.test(href)) return;
    try {
      const url = new URL(href);
      if (url.origin === window.location.origin) return;
    } catch {
      return;
    }
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
}

// Turn each top-level <h2> inside the rendered markdown into a collapsible
// <details> section, containing the heading (as <summary>) and everything
// up to the next <h1>/<h2>. Days then collapse and expand individually.
function wrapDayHeadingsAsToggles(container: HTMLElement): void {
  const directChildren = Array.from(container.children);
  const headings = directChildren.filter((el): el is HTMLHeadingElement => el.tagName === 'H2');
  for (const heading of headings) {
    if (heading.parentElement !== container) continue;
    const details = document.createElement('details');
    details.className = 'day-toggle';
    // Closed by default: a long trip opened flat is a wall of text to scroll
    // past, and the day headings are the map. "Expand all" is one tap away,
    // and setupToggleAll reads the real state, so its label follows.
    details.open = false;

    const summary = document.createElement('summary');
    summary.className = 'day-toggle-summary';
    const chevron = document.createElement('span');
    chevron.className = 'day-toggle-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '▸';
    summary.appendChild(chevron);

    const headingClone = heading.cloneNode(true) as HTMLElement;
    summary.appendChild(headingClone);
    details.appendChild(summary);

    container.insertBefore(details, heading);
    let cursor = heading.nextSibling;
    container.removeChild(heading);
    while (cursor && !(cursor instanceof HTMLElement && /^H[12]$/.test(cursor.tagName))) {
      const next = cursor.nextSibling;
      details.appendChild(cursor);
      cursor = next;
    }
  }
}

// Wires the one bulk control for the per-day toggles. It is a single button
// rather than a pair: it always names the action it will take, so collapsing a
// single day by hand flips it back to "Expand all".
function setupToggleAll(
  container: HTMLElement,
  tools: HTMLElement,
  button: HTMLButtonElement,
): void {
  const toggles = (): HTMLDetailsElement[] =>
    Array.from(container.querySelectorAll<HTMLDetailsElement>('details.day-toggle'));

  const all = toggles();
  // With zero or one day there is nothing to expand "all" of.
  if (all.length < 2) {
    setHidden(tools, true);
    return;
  }
  setHidden(tools, false);

  function sync(): void {
    const anyClosed = toggles().some((d) => !d.open);
    button.textContent = anyClosed ? 'Expand all' : 'Collapse all';
    button.setAttribute('aria-expanded', anyClosed ? 'false' : 'true');
  }

  // Assigned rather than added, so re-rendering the itinerary can never leave
  // two handlers fighting over the same click.
  button.onclick = () => {
    const open = toggles().some((d) => !d.open);
    for (const d of toggles()) d.open = open;
    sync();
  };

  // <details> toggle events do not bubble, so each day needs its own listener.
  for (const d of all) d.addEventListener('toggle', sync);
  sync();
}

// ---------- Trip view ----------
interface Comment {
  id: string;
  name: string;
  text: string;
  createdAt: string;
}

/**
 * Lists the trips this browser still has live access to.
 *
 * The list comes from /api/my-trips, which derives it from the trip cookies the
 * browser already sends — nothing is stored on the device, and a trip whose
 * passcode was rotated drops off on its own. A failure here is silent: the
 * landing page without the list is exactly what it was before, and the tagline
 * below still tells you to open your link.
 */
async function initLanding(): Promise<void> {
  const nav = document.getElementById('landing-trips') as HTMLElement | null;
  const list = document.getElementById('landing-trips-list') as HTMLUListElement | null;
  const tagline = document.getElementById('landing-tagline') as HTMLElement | null;
  if (!nav || !list) return;

  let trips: { slug: string; title: string; startDate?: string }[];
  try {
    const res = await fetch('/api/my-trips', { credentials: 'include' });
    if (!res.ok) return;
    ({ trips } = (await res.json()) as { trips: typeof trips });
  } catch {
    return;
  }
  if (!trips?.length) return;

  for (const trip of trips) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `/${trip.slug}`;
    a.className = 'landing-trip';
    const name = document.createElement('span');
    name.className = 'landing-trip-name';
    name.textContent = trip.title;
    a.appendChild(name);
    if (trip.startDate) {
      const when = document.createElement('span');
      when.className = 'landing-trip-date muted small mono';
      when.textContent = formatTripDate(trip.startDate);
      a.appendChild(when);
    }
    li.appendChild(a);
    list.appendChild(li);
  }

  // Only softened once there is a list to soften it against.
  if (tagline) tagline.textContent = 'Have another link? Open it directly.';
  nav.hidden = false;
}

/** A start date is a plain YYYY-MM-DD, so it is parsed as local rather than UTC. */
function formatTripDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCommentTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

async function initTrip(slug: string): Promise<void> {
  const form = document.getElementById('trip-form') as HTMLFormElement;
  const nameInput = document.getElementById('trip-name') as HTMLInputElement;
  const passcodeInput = document.getElementById('trip-passcode') as HTMLInputElement;
  const errorEl = document.getElementById('trip-error') as HTMLParagraphElement;
  const sections = document.getElementById('trip-sections') as HTMLElement;
  const content = document.getElementById('trip-content') as HTMLElement;
  const itineraryTools = document.getElementById('trip-itinerary-tools') as HTMLElement;
  const toggleAllBtn = document.getElementById('trip-toggle-all') as HTMLButtonElement;
  const commentsSection = document.getElementById('trip-comments-section') as HTMLDetailsElement;
  const commentsList = document.getElementById('trip-comments-list') as HTMLUListElement;
  const commentsCount = document.getElementById('trip-comments-count') as HTMLElement;
  const commentsForm = document.getElementById('trip-comments-form') as HTMLFormElement;
  const commentsLabel = document.getElementById('trip-comment-label') as HTMLElement;
  const commentText = document.getElementById('trip-comment-text') as HTMLTextAreaElement;
  const commentsError = document.getElementById('trip-comments-error') as HTMLParagraphElement;

  let commentsLoaded = false;

  function renderComment(c: Comment): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'comment';
    const header = document.createElement('div');
    header.className = 'comment-header';
    const author = document.createElement('span');
    author.className = 'comment-author';
    author.textContent = c.name;
    const time = document.createElement('span');
    time.className = 'comment-time muted';
    time.textContent = formatCommentTime(c.createdAt);
    header.appendChild(author);
    header.appendChild(time);
    const body = document.createElement('div');
    body.className = 'comment-text';
    body.textContent = c.text;
    li.appendChild(header);
    li.appendChild(body);
    return li;
  }

  function updateCommentsCount(n: number): void {
    commentsCount.textContent = n === 0 ? '' : `(${n})`;
  }

  async function loadComments(): Promise<void> {
    const res = await fetch(`/api/trips/${slug}/comments`, { credentials: 'include' });
    if (res.status !== 200) {
      commentsError.textContent = 'Could not load comments.';
      return;
    }
    const data = (await res.json()) as { comments: Comment[] };
    commentsList.innerHTML = '';
    if (data.comments.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'muted comment-empty';
      empty.textContent = 'No comments yet. Be the first.';
      commentsList.appendChild(empty);
    } else {
      for (const c of data.comments) commentsList.appendChild(renderComment(c));
    }
    updateCommentsCount(data.comments.length);
    commentsLoaded = true;
  }

  async function loadItinerary(): Promise<boolean> {
    const res = await fetch(`/api/trips/${slug}/itinerary`, { credentials: 'include' });
    if (res.status === 200) {
      const data = (await res.json()) as { title?: string; markdown: string; name?: string; partySize?: number };
      content.innerHTML = DOMPurify.sanitize(await marked.parse(data.markdown));
      // Split before enhancing, so each enhancer runs against the section the
      // markup actually ended up in.
      const { costs } = splitTripSections(content);
      openExternalLinksInNewTab(content);
      enhanceRichLinks(content);
      if (costs) enhanceCostTables(costs, slug, data.partySize);
      wrapDayHeadingsAsToggles(content);
      setupToggleAll(content, itineraryTools, toggleAllBtn);
      if (data.title) {
        document.title = data.title;
        // Re-issue the manifest now the trip has a name, so the icon is not
        // labelled "bixl trips" on the home screen.
        scopeManifestToTrip(slug, data.title);
        // iOS ignores the manifest for Add to Home Screen; the icon's label
        // comes from a meta tag instead.
        setHomeScreenName(data.title);
      }
      if (data.name) {
        commentsLabel.textContent = `Posting as ${data.name}`;
      }
      setHidden(sections, false);
      setHidden(form, true);
      setupInstallBanner();
      setupHowToSection();
      // Preload comments so the count is accurate even before the user expands.
      void loadComments();
      return true;
    }
    if (res.status === 404) {
      show('view-notfound');
      return true;
    }
    return false;
  }

  commentsSection.addEventListener('toggle', () => {
    if (commentsSection.open && !commentsLoaded) {
      void loadComments();
    }
  });

  commentsForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    commentsError.textContent = '';
    const text = commentText.value.trim();
    if (!text) return;
    const submitBtn = commentsForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    submitBtn.disabled = true;
    const res = await fetch(`/api/trips/${slug}/comments`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    submitBtn.disabled = false;
    if (res.status === 201) {
      commentText.value = '';
      await loadComments();
      return;
    }
    if (res.status === 401) {
      commentsError.textContent = 'Session expired. Refresh the page and re-enter the passcode.';
      return;
    }
    const data = await res.json().catch(() => ({}));
    commentsError.textContent = `Error: ${(data as { error?: string }).error ?? res.statusText}`;
  });

  if (await loadItinerary()) return;

  setHidden(form, false);
  nameInput.focus();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    const name = nameInput.value.trim();
    const passcode = passcodeInput.value.trim();
    const res = await fetch(`/api/trips/${slug}/access`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passcode, name }),
    });
    if (res.status === 200) {
      passcodeInput.value = '';
      await loadItinerary();
      return;
    }
    if (res.status === 429) {
      errorEl.textContent = 'Too many attempts. Try again later.';
      return;
    }
    if (res.status === 404) {
      show('view-notfound');
      return;
    }
    errorEl.textContent = 'Incorrect passcode.';
  });
}

// ---------- Admin view ----------
async function initAdmin(): Promise<void> {
  const loginForm = document.getElementById('admin-login-form') as HTMLFormElement;
  const passcodeInput = document.getElementById('admin-passcode') as HTMLInputElement;
  const loginError = document.getElementById('admin-error') as HTMLParagraphElement;
  const panel = document.getElementById('admin-panel') as HTMLElement;
  const tripList = document.getElementById('admin-trip-list') as HTMLUListElement;
  const newTripBtn = document.getElementById('admin-new-trip-btn') as HTMLButtonElement;
  const createSection = document.getElementById('admin-create-section') as HTMLElement;
  const cancelCreateBtn = document.getElementById('admin-cancel-create') as HTMLButtonElement;
  const createForm = document.getElementById('admin-create-form') as HTMLFormElement;
  const createError = document.getElementById('admin-create-error') as HTMLParagraphElement;
  const newTitle = document.getElementById('new-title') as HTMLInputElement;
  const newPageId = document.getElementById('new-page-id') as HTMLInputElement;
  const newStartDate = document.getElementById('new-start-date') as HTMLInputElement;
  const newPasscode = document.getElementById('new-passcode') as HTMLInputElement;

  interface Trip {
    slug: string;
    title?: string;
    url: string;
    lastSynced?: string;
    createdAt?: string;
    startDate?: string;
    partySize?: number;
    notionPageId?: string;
    syncStatus?: 'ok' | 'error';
    lastSyncError?: string;
    lastSyncAttempt?: string;
  }

  function formatRelative(iso?: string): string {
    if (!iso) return 'never';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'never';
    const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d ago`;
  }

  async function syncTrip(slug: string, btn: HTMLButtonElement, statusEl: HTMLElement): Promise<void> {
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = 'Syncing…';
    statusEl.textContent = '';
    const res = await fetch(`/api/admin/trips/${slug}/sync`, {
      method: 'POST',
      credentials: 'include',
    });
    if (res.status === 202) {
      btn.textContent = 'Queued ✓';
      statusEl.textContent = 'Sync queued. Refreshing shortly…';
      setTimeout(() => {
        void refreshTrips();
      }, 4000);
      return;
    }
    btn.disabled = false;
    btn.textContent = originalLabel ?? 'Sync';
    if (res.status === 401) {
      statusEl.textContent = 'Session expired. Refresh and sign in again.';
      return;
    }
    if (res.status === 404) {
      statusEl.textContent = 'Trip not found.';
      return;
    }
    statusEl.textContent = `Error: ${res.statusText}`;
  }

  async function updateTrip(
    slug: string,
    body: Record<string, string | number | null>,
  ): Promise<{ ok: boolean; error?: string; resyncTriggered?: boolean }> {
    const res = await fetch(`/api/admin/trips/${slug}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 200) {
      return { ok: true, resyncTriggered: (data as { resyncTriggered?: boolean }).resyncTriggered };
    }
    return { ok: false, error: (data as { error?: string }).error ?? res.statusText };
  }

  async function deleteTrip(slug: string): Promise<{ ok: boolean; error?: string }> {
    const res = await fetch(`/api/admin/trips/${slug}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.status === 200) return { ok: true };
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as { error?: string }).error ?? res.statusText };
  }

  async function updatePasscode(slug: string, passcode: string): Promise<{ ok: boolean; error?: string }> {
    const res = await fetch(`/api/admin/trips/${slug}/passcode`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passcode }),
    });
    if (res.status === 200) return { ok: true };
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as { error?: string }).error ?? res.statusText };
  }

  interface ViewerEntry {
    viewerKey: string;
    name: string;
    viewCount: number;
    firstViewedAt?: string;
    lastViewedAt?: string;
  }

  async function fetchViews(slug: string): Promise<ViewerEntry[] | null> {
    const res = await fetch(`/api/admin/trips/${slug}/views`, { credentials: 'include' });
    if (res.status !== 200) return null;
    const data = (await res.json()) as { views: ViewerEntry[] };
    return data.views;
  }

  async function renameView(slug: string, viewerKey: string, newName: string): Promise<{ ok: boolean; error?: string }> {
    const res = await fetch(
      `/api/admin/trips/${slug}/views/${encodeURIComponent(viewerKey)}/rename`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      },
    );
    if (res.status === 200) return { ok: true };
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as { error?: string }).error ?? res.statusText };
  }

  function renderTripRow(t: Trip): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'trip-row';

    const top = document.createElement('div');
    top.className = 'trip-row-top';

    const main = document.createElement('div');
    main.className = 'trip-row-main';

    const titleLink = document.createElement('a');
    titleLink.href = `/${t.slug}`;
    titleLink.textContent = t.title || t.slug;
    titleLink.className = 'trip-row-title';
    titleLink.target = '_blank';
    titleLink.rel = 'noopener noreferrer';
    main.appendChild(titleLink);

    const meta = document.createElement('div');
    meta.className = 'trip-row-meta';
    const slugCode = document.createElement('code');
    slugCode.textContent = `/${t.slug}`;
    meta.appendChild(slugCode);
    if (t.startDate) {
      const start = document.createElement('span');
      start.className = 'muted';
      start.textContent = `starts ${t.startDate}`;
      meta.appendChild(start);
    }
    const synced = document.createElement('span');
    synced.className = 'muted';
    synced.textContent = `synced ${formatRelative(t.lastSynced)}`;
    meta.appendChild(synced);
    main.appendChild(meta);

    // A failed sync leaves the trip serving its last good content, so without
    // this the row looks healthy — just quietly out of date.
    if (t.syncStatus === 'error') {
      const failure = document.createElement('p');
      failure.className = 'trip-row-sync-error';
      failure.textContent = `Sync failed ${formatRelative(t.lastSyncAttempt)} — ${t.lastSyncError ?? 'unknown error'}`;
      main.appendChild(failure);
    }

    top.appendChild(main);

    const actions = document.createElement('div');
    actions.className = 'trip-row-actions';
    const syncBtn = document.createElement('button');
    syncBtn.type = 'button';
    syncBtn.className = 'secondary';
    syncBtn.textContent = 'Sync';
    const statusEl = document.createElement('span');
    statusEl.className = 'trip-row-status muted';
    syncBtn.addEventListener('click', () => {
      void syncTrip(t.slug, syncBtn, statusEl);
    });

    const passcodeBtn = document.createElement('button');
    passcodeBtn.type = 'button';
    passcodeBtn.className = 'secondary';
    passcodeBtn.textContent = 'Passcode';

    const viewsBtn = document.createElement('button');
    viewsBtn.type = 'button';
    viewsBtn.className = 'secondary';
    viewsBtn.textContent = 'Views';

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'secondary';
    editBtn.textContent = 'Edit';

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'danger';
    deleteBtn.textContent = 'Delete';

    actions.appendChild(syncBtn);
    actions.appendChild(passcodeBtn);
    actions.appendChild(viewsBtn);
    actions.appendChild(editBtn);
    actions.appendChild(deleteBtn);
    actions.appendChild(statusEl);
    top.appendChild(actions);

    deleteBtn.addEventListener('click', async () => {
      const label = t.title || `/${t.slug}`;
      // Deleting takes the comments and view history with it, so make the
      // confirmation deliberate rather than a one-click accident.
      const typed = window.prompt(
        `Delete "${label}" permanently?\n\n` +
          'The link stops working, and its comments, view history, and images are removed. ' +
          'The Notion page itself is untouched.\n\n' +
          `Type the slug ${t.slug} to confirm.`,
      );
      if (typed?.trim() !== t.slug) return;

      deleteBtn.disabled = true;
      deleteBtn.textContent = 'Deleting…';
      const result = await deleteTrip(t.slug);
      if (result.ok) {
        await refreshTrips();
        return;
      }
      deleteBtn.disabled = false;
      deleteBtn.textContent = 'Delete';
      statusEl.textContent = result.error === 'unauthorized'
        ? 'Session expired. Refresh and sign in again.'
        : `Delete failed: ${result.error}`;
    });

    li.appendChild(top);

    // Inline views panel, hidden by default. Lazy-loaded on first open.
    const viewsPanel = document.createElement('div');
    viewsPanel.className = 'trip-row-views';
    viewsPanel.hidden = true;
    li.appendChild(viewsPanel);

    let viewsLoaded = false;
    function renderViewerRow(slug: string, v: ViewerEntry, onRenamed: () => void): HTMLLIElement {
      const item = document.createElement('li');

      const display = document.createElement('div');
      display.className = 'trip-row-views-display';
      const nameEl = document.createElement('span');
      nameEl.className = 'trip-row-views-name';
      nameEl.textContent = v.name;
      const metaEl = document.createElement('span');
      metaEl.className = 'trip-row-views-meta muted small';
      const count = v.viewCount === 1 ? '1 visit' : `${v.viewCount} visits`;
      metaEl.textContent = `${count} · last ${formatRelative(v.lastViewedAt)}`;
      const renameBtn = document.createElement('button');
      renameBtn.type = 'button';
      renameBtn.className = 'secondary small';
      renameBtn.textContent = 'Rename';
      display.appendChild(nameEl);
      display.appendChild(metaEl);
      display.appendChild(renameBtn);

      const editForm = document.createElement('form');
      editForm.className = 'trip-row-views-edit';
      editForm.hidden = true;
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 40;
      input.required = true;
      input.value = v.name;
      const save = document.createElement('button');
      save.type = 'submit';
      save.textContent = 'Save';
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'secondary';
      cancel.textContent = 'Cancel';
      const status = document.createElement('span');
      status.className = 'muted small';
      editForm.appendChild(input);
      editForm.appendChild(save);
      editForm.appendChild(cancel);
      editForm.appendChild(status);

      item.appendChild(display);
      item.appendChild(editForm);

      renameBtn.addEventListener('click', () => {
        display.hidden = true;
        editForm.hidden = false;
        status.textContent = '';
        input.value = v.name;
        input.focus();
        input.select();
      });
      cancel.addEventListener('click', () => {
        editForm.hidden = true;
        display.hidden = false;
        status.textContent = '';
      });
      editForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const newName = input.value.trim();
        if (!newName) { status.textContent = 'Name required.'; return; }
        if (newName === v.name) { cancel.click(); return; }
        save.disabled = true;
        status.textContent = 'Saving…';
        const result = await renameView(slug, v.viewerKey, newName);
        save.disabled = false;
        if (result.ok) {
          status.textContent = 'Saved ✓';
          setTimeout(onRenamed, 600);
          return;
        }
        if (result.error === 'name_taken') {
          status.textContent = 'A viewer with that name already exists.';
          return;
        }
        status.textContent = `Error: ${result.error}`;
      });

      return item;
    }

    function renderViews(slug: string, list: ViewerEntry[]): void {
      viewsPanel.innerHTML = '';
      if (list.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'muted small';
        empty.textContent = 'Nobody has accessed this trip yet.';
        viewsPanel.appendChild(empty);
        return;
      }
      const ul = document.createElement('ul');
      ul.className = 'trip-row-views-list';
      const reload = async () => {
        const fresh = await fetchViews(slug);
        if (fresh) renderViews(slug, fresh);
      };
      for (const v of list) ul.appendChild(renderViewerRow(slug, v, reload));
      viewsPanel.appendChild(ul);
    }

    viewsBtn.addEventListener('click', async () => {
      const opening = viewsPanel.hidden;
      viewsPanel.hidden = !opening;
      viewsBtn.textContent = opening ? 'Hide' : 'Views';
      if (opening && !viewsLoaded) {
        viewsPanel.innerHTML = '<p class="muted small">Loading…</p>';
        const list = await fetchViews(t.slug);
        if (list === null) {
          viewsPanel.innerHTML = '<p class="muted small">Could not load views.</p>';
          return;
        }
        renderViews(t.slug, list);
        viewsLoaded = true;
      }
    });

    // Inline passcode-change form, hidden by default. Toggled by the button.
    const passcodeForm = document.createElement('form');
    passcodeForm.className = 'trip-row-passcode-form';
    passcodeForm.hidden = true;

    const passcodeLabel = document.createElement('label');
    passcodeLabel.className = 'muted small';
    passcodeLabel.textContent = 'New 6-digit passcode';

    const passcodeInputEl = document.createElement('input');
    passcodeInputEl.type = 'text';
    passcodeInputEl.inputMode = 'numeric';
    passcodeInputEl.pattern = '[0-9]{6}';
    passcodeInputEl.maxLength = 6;
    passcodeInputEl.required = true;

    const passcodeSave = document.createElement('button');
    passcodeSave.type = 'submit';
    passcodeSave.textContent = 'Save';

    const passcodeCancel = document.createElement('button');
    passcodeCancel.type = 'button';
    passcodeCancel.className = 'secondary';
    passcodeCancel.textContent = 'Cancel';

    const passcodeStatus = document.createElement('span');
    passcodeStatus.className = 'muted small';

    const passcodeActions = document.createElement('div');
    passcodeActions.className = 'trip-row-passcode-actions';
    passcodeActions.appendChild(passcodeSave);
    passcodeActions.appendChild(passcodeCancel);
    passcodeActions.appendChild(passcodeStatus);

    passcodeForm.appendChild(passcodeLabel);
    passcodeForm.appendChild(passcodeInputEl);
    passcodeForm.appendChild(passcodeActions);
    li.appendChild(passcodeForm);

    passcodeBtn.addEventListener('click', () => {
      const opening = passcodeForm.hidden;
      passcodeForm.hidden = !opening;
      passcodeBtn.textContent = opening ? 'Hide' : 'Passcode';
      passcodeStatus.textContent = '';
      if (opening) {
        passcodeInputEl.value = '';
        passcodeInputEl.focus();
      }
    });
    passcodeCancel.addEventListener('click', () => {
      passcodeForm.hidden = true;
      passcodeBtn.textContent = 'Passcode';
      passcodeStatus.textContent = '';
    });
    passcodeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = passcodeInputEl.value.trim();
      if (!/^\d{6}$/.test(value)) {
        passcodeStatus.textContent = 'Must be exactly 6 digits.';
        return;
      }
      passcodeSave.disabled = true;
      passcodeStatus.textContent = 'Saving…';
      const result = await updatePasscode(t.slug, value);
      passcodeSave.disabled = false;
      if (result.ok) {
        passcodeStatus.textContent = 'Saved ✓';
        passcodeInputEl.value = '';
        setTimeout(() => {
          passcodeForm.hidden = true;
          passcodeBtn.textContent = 'Passcode';
          passcodeStatus.textContent = '';
        }, 1200);
        return;
      }
      passcodeStatus.textContent = `Error: ${result.error}`;
    });

    // Inline edit form: title, Notion source page, and start date. The passcode
    // has its own form above.
    const editForm = document.createElement('form');
    editForm.className = 'trip-row-edit-form';
    editForm.hidden = true;

    const titleLabel = document.createElement('label');
    titleLabel.className = 'muted small';
    titleLabel.textContent = 'Title (replaced by the Notion page title on each sync)';
    const titleField = document.createElement('input');
    titleField.type = 'text';
    titleField.maxLength = 80;
    titleField.value = t.title ?? '';

    const pageLabel = document.createElement('label');
    pageLabel.className = 'muted small';
    pageLabel.textContent = 'Notion page URL or ID (re-syncs on save)';
    const pageField = document.createElement('input');
    pageField.type = 'text';
    pageField.value = t.notionPageId ?? '';
    pageField.placeholder = 'https://www.notion.so/Title-abc123…';

    const startLabel = document.createElement('label');
    startLabel.className = 'muted small';
    startLabel.textContent = 'Start date (for weather; optional)';
    const startField = document.createElement('input');
    startField.type = 'date';
    startField.value = t.startDate ?? '';

    const partyLabel = document.createElement('label');
    partyLabel.className = 'muted small';
    partyLabel.textContent = 'People on the trip (default for the cost split; optional)';
    const partyField = document.createElement('input');
    partyField.type = 'number';
    partyField.min = '1';
    partyField.max = '100';
    partyField.value = t.partySize != null ? String(t.partySize) : '';
    partyField.placeholder = 'e.g. 6';

    const editSave = document.createElement('button');
    editSave.type = 'submit';
    editSave.textContent = 'Save';

    const editCancel = document.createElement('button');
    editCancel.type = 'button';
    editCancel.className = 'secondary';
    editCancel.textContent = 'Cancel';

    const editStatus = document.createElement('span');
    editStatus.className = 'muted small';

    const editActions = document.createElement('div');
    editActions.className = 'trip-row-edit-actions';
    editActions.appendChild(editSave);
    editActions.appendChild(editCancel);
    editActions.appendChild(editStatus);

    editForm.appendChild(titleLabel);
    editForm.appendChild(titleField);
    editForm.appendChild(pageLabel);
    editForm.appendChild(pageField);
    editForm.appendChild(startLabel);
    editForm.appendChild(startField);
    editForm.appendChild(partyLabel);
    editForm.appendChild(partyField);
    editForm.appendChild(editActions);
    li.appendChild(editForm);

    function closeEdit(): void {
      editForm.hidden = true;
      editBtn.textContent = 'Edit';
      editStatus.textContent = '';
    }

    editBtn.addEventListener('click', () => {
      const opening = editForm.hidden;
      editForm.hidden = !opening;
      editBtn.textContent = opening ? 'Hide' : 'Edit';
      editStatus.textContent = '';
      if (opening) {
        titleField.value = t.title ?? '';
        pageField.value = t.notionPageId ?? '';
        startField.value = t.startDate ?? '';
        partyField.value = t.partySize != null ? String(t.partySize) : '';
        titleField.focus();
      }
    });
    editCancel.addEventListener('click', closeEdit);

    editForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const body: Record<string, string | number | null> = {};

      const title = titleField.value.trim();
      if (title && title !== (t.title ?? '')) body.title = title;

      const rawPage = pageField.value.trim();
      if (rawPage) {
        const pageId = extractNotionPageId(rawPage);
        if (!pageId) {
          editStatus.textContent = 'Could not find a Notion page ID in that input.';
          return;
        }
        if (pageId !== t.notionPageId) body.notionPageId = pageId;
      }

      const startDate = startField.value;
      if (startDate !== (t.startDate ?? '')) {
        body.startDate = startDate === '' ? null : startDate;
      }

      const rawParty = partyField.value.trim();
      const party = rawParty === '' ? null : Number(rawParty);
      if (party !== null && (!Number.isInteger(party) || party < 1 || party > 100)) {
        editStatus.textContent = 'People on the trip must be a whole number from 1 to 100.';
        return;
      }
      if (party !== (t.partySize ?? null)) body.partySize = party;

      if (Object.keys(body).length === 0) {
        editStatus.textContent = 'Nothing changed.';
        return;
      }

      editSave.disabled = true;
      editStatus.textContent = 'Saving…';
      const result = await updateTrip(t.slug, body);
      editSave.disabled = false;

      if (!result.ok) {
        editStatus.textContent = result.error === 'unauthorized'
          ? 'Session expired. Refresh and sign in again.'
          : `Error: ${result.error}`;
        return;
      }

      editStatus.textContent = result.resyncTriggered ? 'Saved ✓ Re-syncing…' : 'Saved ✓';
      // A page-ID change kicks off an async sync; give it a moment before the
      // list reloads so the new status is the one shown.
      setTimeout(() => {
        closeEdit();
        void refreshTrips();
      }, result.resyncTriggered ? 4000 : 900);
    });

    return li;
  }

  async function refreshTrips(): Promise<boolean> {
    const res = await fetch('/api/admin/trips', { credentials: 'include' });
    if (res.status !== 200) return false;
    const data = (await res.json()) as { trips: Trip[] };
    tripList.innerHTML = '';
    if (data.trips.length === 0) {
      const li = document.createElement('li');
      li.className = 'muted trip-row-empty';
      li.textContent = 'No trips yet. Click “+ New trip” to create one.';
      tripList.appendChild(li);
    } else {
      const sorted = [...data.trips].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      for (const t of sorted) {
        tripList.appendChild(renderTripRow(t));
      }
    }
    return true;
  }

  function openCreateForm(): void {
    setHidden(createSection, false);
    newTripBtn.disabled = true;
    newTitle.focus();
  }

  function closeCreateForm(): void {
    setHidden(createSection, true);
    newTripBtn.disabled = false;
    createError.textContent = '';
  }

  newTripBtn.addEventListener('click', openCreateForm);
  cancelCreateBtn.addEventListener('click', closeCreateForm);

  if (await refreshTrips()) {
    setHidden(panel, false);
  } else {
    setHidden(loginForm, false);
    passcodeInput.focus();
  }

  loginForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    loginError.textContent = '';
    const passcode = passcodeInput.value;
    const res = await fetch('/api/admin/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passcode }),
    });
    if (res.status === 200) {
      passcodeInput.value = '';
      setHidden(loginForm, true);
      await refreshTrips();
      setHidden(panel, false);
      return;
    }
    if (res.status === 429) {
      loginError.textContent = 'Too many attempts. Try again later.';
      return;
    }
    loginError.textContent = 'Incorrect passcode.';
  });

  createForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    createError.textContent = '';
    const pageId = extractNotionPageId(newPageId.value);
    if (!pageId) {
      createError.textContent = 'Could not find a Notion page ID in that input. Paste a full share URL or the 32-character ID.';
      return;
    }
    const body = {
      title: newTitle.value.trim() || undefined,
      notionPageId: pageId,
      passcode: newPasscode.value.trim(),
      startDate: newStartDate.value || undefined,
    };
    const res = await fetch('/api/admin/trips', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 201) {
      newTitle.value = '';
      newPageId.value = '';
      newStartDate.value = '';
      newPasscode.value = '';
      closeCreateForm();
      await refreshTrips();
      return;
    }
    if (res.status === 401) {
      createError.textContent = 'Session expired. Refresh and sign in again.';
      return;
    }
    const data = await res.json().catch(() => ({}));
    createError.textContent = `Error: ${(data as { error?: string }).error ?? res.statusText}`;
  });
}
